/**
 * Appearance preferences — how LiveContact looks on this device.
 *
 * UI-only state: it lives in localStorage and is never sent to the API. Colour values and
 * scales live in `styles/appearance.css`; this module only knows the option keys, labels
 * and defaults, so the two can't drift into different truths.
 */
export type ThemePreference = 'light' | 'dark' | 'system'
export type AccentPreference = 'blue' | 'violet' | 'teal' | 'emerald' | 'amber' | 'rose'
export type RadiusPreference = 'sharp' | 'default' | 'soft'
export type DensityPreference = 'compact' | 'default' | 'comfortable'
export type MotionPreference = 'system' | 'reduced'

export type AppearancePreferences = {
  theme: ThemePreference
  accent: AccentPreference
  radius: RadiusPreference
  density: DensityPreference
  motion: MotionPreference
}

export const APPEARANCE_STORAGE_KEY = 'lc-appearance'
/** Chakra's own key — kept in sync so its pre-hydration script paints the right mode. */
export const COLOR_MODE_STORAGE_KEY = 'chakra-ui-color-mode'

export const DEFAULT_APPEARANCE: AppearancePreferences = {
  theme: 'system',
  accent: 'blue',
  radius: 'default',
  density: 'default',
  motion: 'system',
}

export const THEME_OPTIONS: ReadonlyArray<ThemePreference> = ['light', 'dark', 'system']

export const ACCENT_OPTIONS: ReadonlyArray<{ value: AccentPreference; label: string }> = [
  { value: 'blue', label: 'Blue' },
  { value: 'violet', label: 'Violet' },
  { value: 'teal', label: 'Teal' },
  { value: 'emerald', label: 'Emerald' },
  { value: 'amber', label: 'Amber' },
  { value: 'rose', label: 'Rose' },
]

export const RADIUS_OPTIONS: ReadonlyArray<{ value: RadiusPreference; label: string }> = [
  { value: 'sharp', label: 'Sharp' },
  { value: 'default', label: 'Default' },
  { value: 'soft', label: 'Soft' },
]

export const DENSITY_OPTIONS: ReadonlyArray<{ value: DensityPreference; label: string }> = [
  { value: 'compact', label: 'Compact' },
  { value: 'default', label: 'Default' },
  { value: 'comfortable', label: 'Comfortable' },
]

const isOneOf = <T extends string>(options: ReadonlyArray<T>, value: unknown): value is T =>
  typeof value === 'string' && (options as ReadonlyArray<string>).includes(value)

const ACCENT_VALUES = ACCENT_OPTIONS.map((o) => o.value)
const RADIUS_VALUES = RADIUS_OPTIONS.map((o) => o.value)
const DENSITY_VALUES = DENSITY_OPTIONS.map((o) => o.value)
const MOTION_VALUES: ReadonlyArray<MotionPreference> = ['system', 'reduced']

/** Keep only the keys we recognise, so a stale or hand-edited entry can never break the UI. */
export const parseAppearance = (raw: unknown): Partial<AppearancePreferences> => {
  if (!raw || typeof raw !== 'object') return {}
  const value = raw as Record<string, unknown>
  const parsed: Partial<AppearancePreferences> = {}
  if (isOneOf(THEME_OPTIONS, value.theme)) parsed.theme = value.theme
  if (isOneOf(ACCENT_VALUES, value.accent)) parsed.accent = value.accent
  if (isOneOf(RADIUS_VALUES, value.radius)) parsed.radius = value.radius
  if (isOneOf(DENSITY_VALUES, value.density)) parsed.density = value.density
  if (isOneOf(MOTION_VALUES, value.motion)) parsed.motion = value.motion
  return parsed
}

export const isDefaultAppearance = (prefs: AppearancePreferences) =>
  (Object.keys(DEFAULT_APPEARANCE) as Array<keyof AppearancePreferences>).every(
    (key) => prefs[key] === DEFAULT_APPEARANCE[key]
  )

export const resolveTheme = (theme: ThemePreference, prefersDark: boolean): 'light' | 'dark' =>
  theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme
